import { Controller } from "@hotwired/stimulus"

// Public "Budget my income" tool — the web twin of budget_assignment_controller.
//
// The maths and the constants are that controller's, copied rather than
// reinvented so the tool behaves exactly like the screen it mirrors:
// FILL_FRACTION, MAX_FILL_PERCENT, the drag mapping, the CTA states.
//
// What is deliberately NOT here: the server. Nothing is submitted, nothing is
// stored, and there is no user — this runs for anonymous visitors on the
// marketing site. Amounts live in the inputs, same as the app, so the DOM
// stays the single source of truth.
//
// The other difference is the lock. In the app a vial is a drag handle for
// reordering until the pencil unlocks it; there is no reordering here, so
// every vial listens from the start and the pencil does not exist.
export default class extends Controller {
  static targets = [
    "hero", "subline", "seg", "segBar", "total", "grid", "cta", "cell",
    "vial", "liquid", "amount", "stepDown", "stepUp", "template",
    "addSheet", "preset"
  ]

  // A vial reads visually full at 40% of the total.
  static FILL_FRACTION = 0.4
  // Liquid never rises past this, so the icon and name always sit on the card
  // background rather than on saturated colour.
  static MAX_FILL_PERCENT = 62
  // Every drag, key press and stepper lands on a round number.
  static STEP = 10
  // BudgetAssignmentsHelper::VIAL_COLORS, in order.
  static COLORS = [
    "oklch(0.68 0.2 264)",
    "oklch(0.8 0.18 70)",
    "oklch(0.62 0.21 305)",
    "oklch(0.72 0.14 185)",
    "oklch(0.62 0.2 278)",
    "oklch(0.7 0.16 220)"
  ]

  connect() {
    this.reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.paint()
  }

  disconnect() {
    this.stopStepRepeat()
  }

  // ── State (the inputs are the source of truth) ───────────────────────────

  amounts() {
    return this.amountTargets.map((input) => this.parse(input.value))
  }

  total() {
    return this.parse(this.totalTarget.value)
  }

  sum() {
    return this.amounts().reduce((acc, value) => acc + value, 0)
  }

  available(index) {
    return Math.max(0, this.total() - (this.sum() - this.amounts()[index]))
  }

  parse(value) {
    return parseInt(String(value).replace(/[^\d]/g, ""), 10) || 0
  }

  money(value) {
    return `$${Math.round(value).toLocaleString("en-US")}`
  }

  setAmount(index, value) {
    const clamped = Math.max(0, Math.min(value, this.available(index)))
    this.amountTargets[index].value = clamped
    this.paint()
  }

  // ── Painting ─────────────────────────────────────────────────────────────

  paint() {
    const amounts   = this.amounts()
    const total     = this.total()
    const fillScale = Math.max(1, total * this.constructor.FILL_FRACTION)
    const sum       = amounts.reduce((acc, value) => acc + value, 0)
    const left      = total - sum
    const done      = total > 0 && left === 0

    amounts.forEach((value, i) => {
      this.liquidTargets[i].style.height =
        `${Math.min(this.constructor.MAX_FILL_PERCENT, (value / fillScale) * 100)}%`
      this.segTargets[i].style.width = total > 0 ? `${Math.min(100, (value / total) * 100)}%` : "0%"
      this.vialTargets[i].setAttribute("aria-valuenow", value)
      this.vialTargets[i].setAttribute("aria-valuemax", value + Math.max(0, left))
      // Never rewrite the field somebody is typing in; blurAmount normalizes it.
      if (document.activeElement !== this.amountTargets[i]) {
        this.amountTargets[i].value = value
      }
      this.stepDownTargets[i].disabled = value <= 0
      this.stepUpTargets[i].disabled   = left <= 0
    })

    this.rollHero(Math.max(0, left))
    this.heroTarget.classList.toggle("done", done)
    this.sublineTarget.textContent = done
      ? "Every dollar has a job"
      : `${this.money(sum)} of ${this.money(total)} assigned`
    this.sublineTarget.classList.toggle("text-success", done)

    this.paintCta(left, done)
  }

  paintCta(left, done) {
    const cta = this.ctaTarget
    cta.classList.remove("ready", "over")
    if (this.total() <= 0) {
      cta.textContent = "Type your income to start"
    } else if (done) {
      cta.classList.add("ready")
      cta.textContent = "Keep this plan in my pocket"
    } else if (left > 0) {
      cta.textContent = `Assign ${this.money(left)} more to finish`
    } else {
      cta.classList.add("over")
      cta.textContent = `${this.money(-left)} over, reduce a budget`
    }
  }

  rollHero(target) {
    const hero = this.heroTarget
    if (this.reduceMotion) {
      hero.dataset.shown = target
      hero.textContent = this.money(target)
      return
    }
    const from = parseInt(hero.dataset.shown || "0", 10)
    hero.dataset.shown = target
    const start = performance.now()
    const tick = (now) => {
      const k = Math.min(1, (now - start) / 280)
      const eased = 1 - Math.pow(1 - k, 3)
      hero.textContent = this.money(Math.round(from + (target - from) * eased))
      if (k < 1 && parseInt(hero.dataset.shown, 10) === target) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }

  // ── Income field ─────────────────────────────────────────────────────────

  formatTotal() {
    this.totalTarget.value = this.parse(this.totalTarget.value).toLocaleString("en-US")
    this.paint()
  }

  // ── Drag ─────────────────────────────────────────────────────────────────

  dragStart(event) {
    // The number is its own control; a press that starts there is a click into
    // the field, not the beginning of a drag.
    if (event.target.closest(".assign-val")) return
    event.preventDefault()

    const vial  = event.currentTarget
    const index = Number(vial.dataset.index)
    const rect  = vial.getBoundingClientRect()
    // The drag spans everything this vial can take (its value plus whatever is
    // left), never less than the visual fill scale — otherwise a vial reads
    // full while money remains and only the steppers can assign the rest.
    const fillScale = Math.max(1, this.total() * this.constructor.FILL_FRACTION)
    const dragMax   = Math.max(fillScale, this.available(index))

    vial.setPointerCapture(event.pointerId)
    vial.classList.add("grabbing")
    this.cellTargets[index].classList.add("filling")

    const move = (ev) => {
      const fraction = Math.max(0, Math.min(1, 1 - (ev.clientY - rect.top) / rect.height))
      const step = this.constructor.STEP
      this.setAmount(index, Math.round((fraction * dragMax) / step) * step)
    }
    const stop = () => {
      vial.classList.remove("grabbing")
      this.cellTargets[index].classList.remove("filling")
      vial.removeEventListener("pointermove", move)
      vial.removeEventListener("pointerup", stop)
      vial.removeEventListener("pointercancel", stop)
    }

    vial.addEventListener("pointermove", move)
    vial.addEventListener("pointerup", stop)
    vial.addEventListener("pointercancel", stop)
    move(event)
  }

  // ── Keyboard slider ──────────────────────────────────────────────────────

  keyAdjust(event) {
    const index = Number(event.currentTarget.dataset.index)
    const value = this.amounts()[index]
    const step  = this.constructor.STEP
    const map = {
      ArrowUp: value + step,
      ArrowRight: value + step,
      ArrowDown: value - step,
      ArrowLeft: value - step,
      PageUp: value + step * 4,
      PageDown: value - step * 4,
      Home: 0,
      End: this.available(index)
    }
    if (!(event.key in map)) return
    event.preventDefault()
    this.setAmount(index, map[event.key])
  }

  // ── Typing an amount ─────────────────────────────────────────────────────

  selectAmount(event) {
    event.target.select()
  }

  typeAmount(event) {
    this.setAmount(Number(event.target.dataset.index), this.parse(event.target.value))
  }

  blurAmount() {
    this.paint()
  }

  // ── Steppers, with press-and-hold repeat ─────────────────────────────────

  stepStart(event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.index)
    const dir   = Number(event.currentTarget.dataset.dir)
    const tick  = () => this.setAmount(index, this.amounts()[index] + dir * this.constructor.STEP)

    tick()
    this.stopStepRepeat()
    this.stepDelay = setTimeout(() => {
      this.stepTimer = setInterval(tick, 70)
    }, 380)

    const stop = () => {
      this.stopStepRepeat()
      window.removeEventListener("pointerup", stop)
      window.removeEventListener("pointercancel", stop)
    }
    window.addEventListener("pointerup", stop)
    window.addEventListener("pointercancel", stop)
  }

  stopStepRepeat() {
    clearTimeout(this.stepDelay)
    clearInterval(this.stepTimer)
  }

  // ── Quick actions ────────────────────────────────────────────────────────

  fillSavings() {
    const index = this.amountTargets.findIndex((input) => input.dataset.savings)
    if (index < 0) return
    this.setAmount(index, this.amounts()[index] + Math.max(0, this.total() - this.sum()))
  }

  splitEvenly() {
    const total = this.total()
    const count = this.amountTargets.length
    if (total <= 0 || count === 0) return

    const step = this.constructor.STEP
    const each = Math.floor(total / count / step) * step
    this.amountTargets.forEach((input) => { input.value = each })
    this.amountTargets[0].value = each + (total - each * count)
    this.paint()
  }

  clearAll() {
    this.amountTargets.forEach((input) => { input.value = 0 })
    this.paint()
  }

  // ── Add and remove budgets ───────────────────────────────────────────────

  openAddSheet() {
    this.syncPresets()
    this.addSheetTarget.classList.remove("hidden")
  }

  closeAddSheet() {
    this.addSheetTarget.classList.add("hidden")
  }

  // A preset already on the board cannot be added twice.
  syncPresets() {
    const names = this.cellTargets.map((cell) => cell.dataset.name)
    this.presetTargets.forEach((button) => {
      button.disabled = names.includes(button.dataset.name)
    })
  }

  addPreset(event) {
    const button = event.currentTarget
    const cell   = this.templateTarget.content.firstElementChild.cloneNode(true)

    cell.dataset.name = button.dataset.name
    cell.querySelector("[data-role='icon']").src = button.dataset.iconSrc
    cell.querySelector("[data-role='name']").textContent = button.dataset.name
    cell.querySelectorAll("[aria-label]").forEach((node) => {
      node.setAttribute("aria-label", node.getAttribute("aria-label").replace("__NAME__", button.dataset.name))
    })

    // Savings stays last: it is the budget "put the rest in Savings" fills, and
    // anything below it would read as the end of the list instead.
    const savingsCell = this.cellTargets.find((c) => c.querySelector("[data-savings]"))
    this.gridTarget.insertBefore(cell, savingsCell || null)

    this.reindex()
    this.closeAddSheet()
    this.paint()
  }

  removeBudget(event) {
    // Never leave the tool with nothing to assign to.
    if (this.cellTargets.length <= 1) return
    event.currentTarget.closest("[data-vial-cell]").remove()
    this.reindex()
    this.paint()
  }

  // Indexes are positional: every target list is read by position, so a cell
  // coming or going has to renumber the whole board and re-colour it, the same
  // way vial_color(index) does on the server.
  reindex() {
    this.cellTargets.forEach((cell, i) => {
      cell.style.setProperty("--vial-c", this.constructor.COLORS[i % this.constructor.COLORS.length])
      cell.querySelectorAll("[data-index]").forEach((node) => { node.dataset.index = i })
    })
    this.segBarTarget.innerHTML = ""
    this.cellTargets.forEach((cell, i) => {
      const seg = document.createElement("i")
      seg.dataset.toolAssignTarget = "seg"
      seg.dataset.index = i
      seg.style.setProperty("--vial-c", this.constructor.COLORS[i % this.constructor.COLORS.length])
      this.segBarTarget.appendChild(seg)
    })
  }
}
